import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import AttachmentTable, { type AttachmentTableRow } from '../../../components/ui/AttachmentTable'
import AttachmentPreviewPanel from '../../../components/ui/AttachmentPreviewPanel'
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton'
import { useToast } from '../../../components/ui/ToastProvider'
import { GRANTS } from '../../../auth/grants'
import { useAttachmentPreview } from '../../../lib/useAttachmentPreview'
import { useTaskAttachments } from '../hooks/useMeetingManagement'

/** Pop-up'ın başlıkta göstermek için ihtiyaç duyduğu **asgari** satır şekli. */
export interface TaskFilesTarget {
  id: number
  name: string
}

interface Props {
  /** Açık görev; `null` → pop-up kapalı (ve dosya isteği hiç atılmaz). */
  task: TaskFilesTarget | null
  onHide: () => void
}

/**
 * **Toplantı Yönetimi — Görev Dosyaları** pop-up'ı (105/FR-052): satırdaki ataca tıklanınca açılır,
 * görevin eklerini listeler; her satır **indirilebilir**, PDF ve görseller pop-up'ın **içinde
 * görüntülenebilir**.
 *
 * <p><b>Salt okunurdur</b> — Prensip Kararları'nın kardeş pop-up'ından ayrıldığı tek nokta budur:
 * yükleme alanı ve "Kaldır" işlemi <b>yoktur</b>. Bu ekran tamamlanmış görevleri toplantı masasında
 * karara bağlar; dosyayı ekleyen taraf görevin sorumlusudur (Görev Detay → Dokümanlar, ya da onaya
 * gönderirken açılan not pop-up'ı). Buraya yazma koymak, ekranın yetki modelini (kapatma/yeniden
 * açma) dosya yönetimine de genişletirdi.
 *
 * <p><b>Ataç zaten "dosya var" demektir</b> (satır yalnız {@code hasAttachment} ise ataç çizer), bu
 * yüzden pop-up pratikte hiç boş açılmaz; yine de liste yarışabilir (dosya bu arada silinmiş
 * olabilir) — boş hâl için metin vardır, boş bir kutu bırakılmaz.
 */
export default function TaskFilesDialog({ task, onHide }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const taskId = task?.id ?? null
  const { data, isPending } = useTaskAttachments(taskId)

  // Önizleme durumu/ömrü ortak hook'ta; kapsam kimliği görev kimliğidir, yani pop-up kapanıp başka
  // bir görev açıldığında bekleyen indirme atılır (bkz. `useAttachmentPreview`).
  const onPreviewError = useCallback(
    () => toast.error(t('meetingManagement.files.previewError')),
    [toast, t],
  )
  const { preview, loading, open, close } = useAttachmentPreview({
    scopeId: taskId,
    onError: onPreviewError,
  })

  const rows: AttachmentTableRow[] = (data ?? []).map((d) => ({
    key: String(d.id),
    fileName: d.fileName,
    extension: d.extension,
    size: d.size,
    downloadId: d.id,
    uploadedByName: d.uploadedByName,
    uploadedAt: d.uploadedAt,
  }))

  // İlk açılışta liste henüz yokken "dosya yok" yazmak yanlış olurdu — ataç zaten dosya olduğunu
  // söylüyor; iskelet, boş metinle yarışmaz.
  const list = isPending ? (
    <LoadingSkeleton />
  ) : (
    <AttachmentTable
      rows={rows}
      emptyText={t('meetingManagement.files.empty')}
      onPreview={open}
      busy={loading}
      // Bağlam: ATTACHED_FILE.TABLE_NAME = 'MEETING_MINUTES' (görev dokümanları) — Görev Detay'ın
      // Dokümanlar bölümüyle AYNI yetki; iki yüzey aynı dosyayı gösterir, kapıları ayrışamaz.
      requiredGrant={GRANTS.MEETING_FILE_DOWNLOAD}
    />
  )

  return (
    <FormDialog
      visible={task != null}
      header={t('meetingManagement.files.title')}
      /* Footer YOK: pop-up salt okunurdur, kaydedilecek bir şey yoktur — kapanış ×/Esc ile.
         `submitLabel`/`onSubmit` yine de zorunlu prop'lar; hideFooter ikisini de etkisiz kılar. */
      hideFooter
      submitLabel=""
      onSubmit={onHide}
      onHide={onHide}
      /* Prensip Kararları'nın dosya pop-up'ıyla AYNI genişlik: beş kolonlu tablo daha darında dosya
         adlarını erkenden kısaltıyordu, önizleme paneli de bu genişlikten yararlanır. */
      width="64rem"
    >
      {task != null && (
        <p className="ui-dialog-subject" title={task.name}>
          {task.name}
        </p>
      )}

      {preview != null ? <AttachmentPreviewPanel preview={preview} onBack={close} /> : list}
    </FormDialog>
  )
}
